/**
 * ICE restart no peer atual do RealtimeKit, antes de leaveRoom + join novo.
 *
 * O SDK 1.5.1 declara `Transport.restartIce()`, mas o bundle não chama esse
 * método em lugar nenhum. A implementação só faz `createOffer({ iceRestart })`
 * e devolve o SDP — não dá `setLocalDescription` e não manda nada ao SFU.
 * Chamar isso e parar seria um no-op.
 *
 * O peer real está em `meeting.__internals__.callStats.backend.{producing,
 * consuming}Transport.handler.pc` (o BFS do meeting não acha o PC: o transport
 * mora em WeakMap). O socket de mídia também está em WeakMap; capturamos o
 * objeto que tem `sendMessagePromise` + `roomName` quando o SDK o guarda.
 *
 * A troca usa `renegotiateSessionDescription` (evento de mídia `50331648 | 50`),
 * com o `transport_id` que já existe. Isso atualiza o mesmo transport no SFU.
 * Não usamos `createWebRTCTransport`: esse evento aloca um transport novo.
 */

/** Namespace dos eventos de mídia no RealtimeKit 1.5 (`cf(of, 50331648)`). */
const MEDIA_EVENT_NS = 0x03000000;
const RENEGOTIATE_SESSION_DESCRIPTION = MEDIA_EVENT_NS | 50;

const SIGNAL_TIMEOUT_MS = 3_500;
const ICE_RECOVER_MS = 6_000;
const MAX_TRACKED_SOCKETS = 4;

export type IceRestartResult = "recovered" | "unavailable" | "failed";

type SignalingSocket = {
    roomName?: string;
    sendMessagePromise?: (
        event: number,
        protobuf: Uint8Array,
    ) => Promise<{ payload?: unknown }>;
    sendMessagePromiseWithTimeout?: (args: {
        event: number;
        timeout: number;
        protobuf: Uint8Array;
    }) => Promise<{ payload?: unknown }>;
};

type TransportLike = {
    serverId?: string;
    direction?: string;
    closed?: boolean;
    handler?: { pc?: RTCPeerConnection };
};

type RestartOutcome = {
    before: string;
    applied: boolean;
    connected: boolean;
    dirty: boolean;
};

const signalingSockets: SignalingSocket[] = [];
let captureInstalled = false;

function rememberSignalingSocket(socket: SignalingSocket): void {
    const existing = signalingSockets.indexOf(socket);
    if (existing >= 0) {
        signalingSockets.splice(existing, 1);
    }
    signalingSockets.push(socket);
    if (signalingSockets.length > MAX_TRACKED_SOCKETS) {
        signalingSockets.shift();
    }
}

/** Mais recente primeiro: o socket de mídia costuma ser o último a ser criado. */
export function getSignalingSockets(): SignalingSocket[] {
    return signalingSockets.slice().reverse();
}

export function installSignalingCapture(): void {
    if (captureInstalled || typeof WeakMap === "undefined") {
        return;
    }
    const native = WeakMap.prototype.set;
    if (typeof native !== "function") {
        return;
    }
    const wrapped = function <K extends object, V>(
        this: WeakMap<K, V>,
        key: K,
        value: V,
    ): WeakMap<K, V> {
        if (
            value !== null &&
            typeof value === "object" &&
            typeof (value as SignalingSocket).sendMessagePromise === "function" &&
            typeof (value as SignalingSocket).roomName === "string"
        ) {
            rememberSignalingSocket(value as SignalingSocket);
        }
        return native.call(this, key, value);
    };
    try {
        // eslint-disable-next-line no-extend-native -- socket de mídia do RealtimeKit só existe dentro de WeakMap
        WeakMap.prototype.set = wrapped;
        captureInstalled = true;
    } catch {
        captureInstalled = false;
    }
}

function encodeVarint(value: number): Uint8Array {
    const bytes: number[] = [];
    let rest = value >>> 0;
    while (rest > 0x7f) {
        bytes.push((rest & 0x7f) | 0x80);
        rest >>>= 7;
    }
    bytes.push(rest);
    return Uint8Array.from(bytes);
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const part of parts) {
        out.set(part, offset);
        offset += part.length;
    }
    return out;
}

function encodeKey(fieldNumber: number, wireType: number): Uint8Array {
    return encodeVarint((fieldNumber << 3) | wireType);
}

function encodeUtf8(value: string): Uint8Array {
    if (typeof TextEncoder !== "undefined") {
        return new TextEncoder().encode(value);
    }
    const bytes: number[] = [];
    for (let i = 0; i < value.length; i++) {
        let code = value.charCodeAt(i);
        if (code >= 0xd800 && code <= 0xdbff && i + 1 < value.length) {
            const next = value.charCodeAt(i + 1);
            if (next >= 0xdc00 && next <= 0xdfff) {
                code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
                i += 1;
            }
        }
        if (code < 0x80) {
            bytes.push(code);
        } else if (code < 0x800) {
            bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
        } else if (code < 0x10000) {
            bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        } else {
            bytes.push(
                0xf0 | (code >> 18),
                0x80 | ((code >> 12) & 0x3f),
                0x80 | ((code >> 6) & 0x3f),
                0x80 | (code & 0x3f),
            );
        }
    }
    return Uint8Array.from(bytes);
}

function decodeUtf8(bytes: Uint8Array): string {
    if (typeof TextDecoder !== "undefined") {
        return new TextDecoder().decode(bytes);
    }
    let out = "";
    for (let i = 0; i < bytes.length; i++) {
        out += String.fromCharCode(bytes[i]);
    }
    return out;
}

function encodeStringField(fieldNumber: number, value: string): Uint8Array {
    const data = encodeUtf8(value);
    return concatBytes([encodeKey(fieldNumber, 2), encodeVarint(data.length), data]);
}

function encodeVarintField(fieldNumber: number, value: number): Uint8Array {
    return concatBytes([encodeKey(fieldNumber, 0), encodeVarint(value)]);
}

function encodeMessageField(fieldNumber: number, message: Uint8Array): Uint8Array {
    return concatBytes([encodeKey(fieldNumber, 2), encodeVarint(message.length), message]);
}

/**
 * `media.RenegotiateRequest`: transport_id (1) + SessionDescription (2).
 * SessionDescription: target enum (1), type (2), sdp (3).
 * target 0 = PUBLISHER (send), 1 = SUBSCRIBER (recv).
 */
export function encodeRenegotiateRequest(input: {
    transportId: string;
    target: 0 | 1;
    type: string;
    sdp: string;
}): Uint8Array {
    const description = concatBytes([
        encodeVarintField(1, input.target),
        encodeStringField(2, input.type),
        encodeStringField(3, input.sdp),
    ]);
    return concatBytes([
        encodeStringField(1, input.transportId),
        encodeMessageField(2, description),
    ]);
}

function readVarint(buf: Uint8Array, offset: number): { value: number; offset: number } {
    let value = 0;
    let shift = 0;
    let cursor = offset;
    while (cursor < buf.length && shift <= 28) {
        const byte = buf[cursor];
        cursor += 1;
        value |= (byte & 0x7f) << shift;
        if ((byte & 0x80) === 0) {
            return { value: value >>> 0, offset: cursor };
        }
        shift += 7;
    }
    throw new Error("protobuf varint inválido");
}

function skipField(buf: Uint8Array, offset: number, wireType: number): number {
    if (wireType === 0) {
        return readVarint(buf, offset).offset;
    }
    if (wireType === 1) {
        return offset + 8;
    }
    if (wireType === 5) {
        return offset + 4;
    }
    if (wireType === 2) {
        const length = readVarint(buf, offset);
        return length.offset + length.value;
    }
    throw new Error("protobuf wire type não suportado");
}

/** Decodifica a resposta (mesmo formato do request: transport_id + description). */
export function decodeRenegotiateResponse(payload: Uint8Array): {
    transportId: string;
    target: number;
    type: string;
    sdp: string;
} {
    let transportId = "";
    let target = 0;
    let type = "";
    let sdp = "";
    let offset = 0;

    const readDescription = (message: Uint8Array) => {
        let cursor = 0;
        while (cursor < message.length) {
            const key = readVarint(message, cursor);
            const field = key.value >>> 3;
            const wire = key.value & 0x07;
            cursor = key.offset;
            if (wire === 0 && field === 1) {
                const enumValue = readVarint(message, cursor);
                target = enumValue.value;
                cursor = enumValue.offset;
                continue;
            }
            if (wire === 2) {
                const length = readVarint(message, cursor);
                const start = length.offset;
                const text = decodeUtf8(message.subarray(start, start + length.value));
                if (field === 2) {
                    type = text;
                } else if (field === 3) {
                    sdp = text;
                }
                cursor = start + length.value;
                continue;
            }
            cursor = skipField(message, cursor, wire);
        }
    };

    while (offset < payload.length) {
        const key = readVarint(payload, offset);
        const field = key.value >>> 3;
        const wire = key.value & 0x07;
        offset = key.offset;
        if (wire === 2) {
            const length = readVarint(payload, offset);
            const start = length.offset;
            const slice = payload.subarray(start, start + length.value);
            if (field === 1) {
                transportId = decodeUtf8(slice);
            } else if (field === 2) {
                readDescription(slice);
            }
            offset = start + length.value;
            continue;
        }
        offset = skipField(payload, offset, wire);
    }

    if (!type || !sdp) {
        throw new Error("resposta de renegotiate sem SDP");
    }
    return { transportId, target, type, sdp };
}

function asBytes(payload: unknown): Uint8Array {
    if (payload instanceof Uint8Array) {
        return payload;
    }
    if (payload instanceof ArrayBuffer) {
        return new Uint8Array(payload);
    }
    if (ArrayBuffer.isView(payload)) {
        return new Uint8Array(payload.buffer, payload.byteOffset, payload.byteLength);
    }
    throw new Error("resposta de renegotiate sem payload");
}

function isPeerConnection(value: unknown): value is RTCPeerConnection {
    return typeof RTCPeerConnection !== "undefined" && value instanceof RTCPeerConnection;
}

function readTransport(value: unknown): TransportLike | null {
    if (!value || typeof value !== "object") {
        return null;
    }
    const transport = value as TransportLike;
    if (!isPeerConnection(transport.handler?.pc)) {
        return null;
    }
    if (typeof transport.serverId !== "string" || transport.serverId.length === 0) {
        return null;
    }
    if (transport.closed) {
        return null;
    }
    return transport;
}

function findTransports(meeting: unknown): TransportLike[] {
    const root = meeting as {
        __internals__?: {
            callStats?: { backend?: Record<string, unknown> | null };
        };
    } | null;
    const backend = root?.__internals__?.callStats?.backend;
    if (!backend) {
        return [];
    }
    const found: TransportLike[] = [];
    const seen = new Set<RTCPeerConnection>();
    for (const key of ["producingTransport", "consumingTransport"] as const) {
        const transport = readTransport(backend[key]);
        const pc = transport?.handler?.pc;
        if (!transport || !pc || seen.has(pc)) {
            continue;
        }
        if (!transport.direction) {
            transport.direction = key === "consumingTransport" ? "recv" : "send";
        }
        seen.add(pc);
        found.push(transport);
    }
    return found;
}

export function canTryIceRestart(meeting: unknown): boolean {
    try {
        return findTransports(meeting).length > 0 && getSignalingSockets().length > 0;
    } catch {
        return false;
    }
}

async function rollbackLocalOffer(pc: RTCPeerConnection): Promise<boolean> {
    if (pc.signalingState !== "have-local-offer") {
        return true;
    }
    try {
        await pc.setLocalDescription({ type: "rollback" });
        return pc.signalingState !== "have-local-offer";
    } catch (err) {
        console.warn("Rollback do ICE restart falhou:", err);
        return false;
    }
}

function waitForIceConnected(pc: RTCPeerConnection, timeoutMs: number): Promise<boolean> {
    return new Promise((resolve) => {
        let settled = false;
        const started = Date.now();
        // "connected" no instante do answer ainda é o par antigo. Só contamos
        // recuperação depois de um flap (checking/disconnected/failed) ou de
        // 1,5s estável já com a answer nova aplicada.
        let sawRestart =
            pc.iceConnectionState !== "connected" &&
            pc.iceConnectionState !== "completed";
        const finish = (ok: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            pc.removeEventListener("iceconnectionstatechange", onChange);
            window.clearInterval(poll);
            window.clearTimeout(cap);
            resolve(ok);
        };
        const iceUp = () =>
            pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed";
        const onChange = () => {
            const state = pc.iceConnectionState;
            if (state !== "connected" && state !== "completed") {
                sawRestart = true;
            }
            const elapsed = Date.now() - started;
            if (iceUp() && (sawRestart || elapsed >= 1_500)) {
                finish(true);
                return;
            }
            if ((state === "failed" || state === "closed") && elapsed > 400) {
                finish(false);
            }
        };
        const poll = window.setInterval(onChange, 250);
        const cap = window.setTimeout(() => finish(iceUp()), timeoutMs);
        pc.addEventListener("iceconnectionstatechange", onChange);
    });
}

async function signalRenegotiate(
    socket: SignalingSocket,
    protobuf: Uint8Array,
): Promise<Uint8Array> {
    if (typeof socket.sendMessagePromiseWithTimeout === "function") {
        const response = await socket.sendMessagePromiseWithTimeout({
            event: RENEGOTIATE_SESSION_DESCRIPTION,
            timeout: SIGNAL_TIMEOUT_MS,
            protobuf,
        });
        return asBytes(response?.payload);
    }
    if (typeof socket.sendMessagePromise !== "function") {
        throw new Error("socket sem sendMessagePromise");
    }
    const response = await Promise.race([
        socket.sendMessagePromise(RENEGOTIATE_SESSION_DESCRIPTION, protobuf),
        new Promise<never>((_, reject) => {
            window.setTimeout(
                () => reject(new Error("renegotiate timeout")),
                SIGNAL_TIMEOUT_MS,
            );
        }),
    ]);
    return asBytes(response?.payload);
}

async function restartTransport(
    transport: TransportLike,
    sockets: SignalingSocket[],
): Promise<RestartOutcome> {
    const pc = transport.handler?.pc;
    if (!pc) {
        return { before: "closed", applied: false, connected: false, dirty: false };
    }
    const before = pc.iceConnectionState;
    if (pc.connectionState === "closed" || pc.signalingState !== "stable") {
        return { before, applied: false, connected: false, dirty: false };
    }
    if (typeof pc.createOffer !== "function") {
        return { before, applied: false, connected: false, dirty: false };
    }

    if (typeof pc.restartIce === "function") {
        pc.restartIce();
    }
    const offer = await pc.createOffer({ iceRestart: true });
    if (!offer.sdp) {
        return { before, applied: false, connected: false, dirty: false };
    }
    await pc.setLocalDescription(offer);

    const target: 0 | 1 = transport.direction === "recv" ? 1 : 0;
    const request = encodeRenegotiateRequest({
        transportId: transport.serverId || "",
        target,
        type: offer.type || "offer",
        sdp: offer.sdp,
    });

    let answer: { type: string; sdp: string } | null = null;
    let lastError: unknown = null;
    for (const socket of sockets.slice(0, 2)) {
        try {
            const payload = await signalRenegotiate(socket, request);
            const decoded = decodeRenegotiateResponse(payload);
            answer = { type: decoded.type, sdp: decoded.sdp };
            break;
        } catch (err) {
            lastError = err;
        }
    }

    if (!answer) {
        const clean = await rollbackLocalOffer(pc);
        if (lastError) {
            console.warn("ICE restart sem answer do SFU:", lastError);
        }
        return { before, applied: false, connected: false, dirty: !clean };
    }

    try {
        await pc.setRemoteDescription({
            type: answer.type as RTCSdpType,
            sdp: answer.sdp,
        });
    } catch (err) {
        const clean = await rollbackLocalOffer(pc);
        console.warn("setRemoteDescription do ICE restart falhou:", err);
        return { before, applied: false, connected: false, dirty: !clean };
    }

    const connected = await waitForIceConnected(pc, ICE_RECOVER_MS);
    return { before, applied: true, connected, dirty: false };
}

function sessionRecovered(outcomes: RestartOutcome[]): boolean {
    if (outcomes.length === 0 || outcomes.some((outcome) => outcome.dirty)) {
        return false;
    }
    const broken = outcomes.filter(
        (outcome) => outcome.before === "failed" || outcome.before === "disconnected",
    );
    const required = broken.length > 0 ? broken : outcomes;
    return required.every((outcome) => outcome.applied && outcome.connected);
}

/**
 * Reinicia o ICE dos transports vivos e espera a mídia voltar.
 * Não chama leaveRoom e não mexe no mirror de áudio.
 */
export async function tryIceRestart(meeting: unknown): Promise<IceRestartResult> {
    let transports: TransportLike[] = [];
    try {
        transports = findTransports(meeting);
    } catch (err) {
        console.warn("Não foi possível ler os transports para ICE restart:", err);
        return "unavailable";
    }
    const sockets = getSignalingSockets();
    if (transports.length === 0 || sockets.length === 0) {
        return "unavailable";
    }

    const outcomes: RestartOutcome[] = [];
    for (const transport of transports) {
        try {
            outcomes.push(await restartTransport(transport, sockets));
        } catch (err) {
            console.warn("ICE restart lançou:", err);
            const pc = transport.handler?.pc;
            const dirty = pc ? !(await rollbackLocalOffer(pc)) : false;
            outcomes.push({
                before: pc?.iceConnectionState ?? "failed",
                applied: false,
                connected: false,
                dirty,
            });
        }
    }

    return sessionRecovered(outcomes) ? "recovered" : "failed";
}

if (typeof process === "undefined" || process.env.NODE_ENV !== "test") {
    installSignalingCapture();
}
